/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	dataEqual,
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	importSchema,
	IRouteListEntry,
	ISchemaConstObject,
	ISchemaValue,
	serializeIDTToYAML,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { IIconPackageURLMapping } from "@adapptio/lib-components";
import {
	BlueprintBase,
	IBlueprintMetadata,
	IResourceDescription,
	RESOURCE_TYPES
} from "@adapptio/lib-core";
import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { getResourcePath, normalizeResourcePath, parseBasePath } from "./ResourceHelpers";
import {
	BLUEPRINT_RESOURCE_TYPES,
	IEditorResource_Directory,
	IEditorResourceDeleteEvent,
	IIconPack,
	IResourceChanges,
	TBlueprintResourceTypes,
	TEditorBlueprintResource,
	TEditorResource,
	TEditorResourceByType
} from "./ResourceTypes";

const RELOAD_INTERVAL = 10000;

/**
 * Service to manage resources
 */
export class ResourceService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** API Service Instance */
	private apiService: IEditorApiService;

	/** Map of (resource type -> view name) to open resource */
	private resourceTypeView: { [K: string]: string } = {};

	/** Fallback resource type view */
	private fallbackResourceTypeView: string | null = null;

	/** Map of (resource type -> commandId) to create a new resource  */
	private resourceTypeCreateCommand: { [K: string]: string } = {};

	/** Reload setTimeout ref */
	private reloadTimer: unknown;

	/** Local resource list */
	private resourceList: TEditorResource[] = [];

	/** Index of resources by type */
	private resourceIndexByType: { [K: string]: TEditorResource[] } = {};

	/** Index of resources by ID */
	private resourceIndexById: { [K: string]: TEditorResource } = {};

	/** Index of resources by path */
	private resourceIndexByPath: { [K: string]: TEditorResource } = {};

	/** Index of reverse resource dependencies */
	private referencedByIndex: Map<string, Set<string>> = new Map();

	/** Route list */
	private routeList: IRouteListEntry[] = [];

	/** Index of routes by name */
	private routeIndex: { [K: string]: IRouteListEntry } = {};

	/** Icon packs */
	private iconPacks: IIconPack[] = [];

	/** Index of icon packs by name */
	private iconPackIndexByName: { [K: string]: IIconPack };

	/** Icon pack to URL mapping */
	private iconPackUrlMapping: IIconPackageURLMapping = {};

	private initialized = false;

	private readonly onInit = createEventEmitter<void>();

	/** Event emitted when resource list has changed */
	public readonly onResourceListChange = createEventEmitter<void>();

	/** Event emitted when a resource with given type changed */
	public readonly onResourceTypeChange = createEventEmitter<RESOURCE_TYPES>();

	/** Event emitted when a resource content has changed */
	public readonly onResourceContentChange = createEventEmitter<TEditorResource>();

	/** Event emitted when a route list changed */
	public readonly onRouteListChange = createEventEmitter<RESOURCE_TYPES>();

	/** Event emitted when icons has changed */
	public readonly onIconPacksChanged = createEventEmitter<void>();

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.apiService = dc.get("apiService");
		this.logger = dc.get("logger").facility("ResourceService");
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		await this.loadResourceList();
		await this.loadRouteList();
		await this.loadIconPacks();

		this.handleReload(false);

		this.initialized = true;
		emitEvent(this.onInit);
	}

	public isInitialized(): boolean {
		return this.initialized;
	}

	public async waitForInit(): Promise<void> {
		return new Promise((resolve) => {
			if (this.isInitialized()) {
				return resolve();
			}

			const handleInit = () => {
				offEvent(this.onInit, handleInit);

				return resolve();
			};

			onEvent(this.onInit, handleInit);
		});
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		removeAllEventListeners(this.onResourceContentChange);
		removeAllEventListeners(this.onResourceListChange);
		removeAllEventListeners(this.onResourceTypeChange);
		removeAllEventListeners(this.onRouteListChange);
		removeAllEventListeners(this.onIconPacksChanged);
		removeAllEventListeners(this.onInit);

		if (this.reloadTimer) {
			clearTimeout(this.reloadTimer as number);
		}
	}

	/**
	 * Registers view for a given resource type
	 *
	 * @param resourceType Resource type
	 * @param viewName View name
	 */
	public registerResourceView(resourceType: RESOURCE_TYPES, viewName: string): void {
		if (this.resourceTypeView[resourceType as string]) {
			throw new Error(`View for a resource type '${resourceType}' is already registered.`);
		}

		this.resourceTypeView[resourceType as string] = viewName;
	}

	public registerFallbackResourceView(viewName: string): void {
		if (this.fallbackResourceTypeView) {
			throw new Error(`Fallback resource view is already registered: ${this.fallbackResourceTypeView}`);
		}

		this.fallbackResourceTypeView = viewName;
	}

	/**
	 * Returns view name registered for a given resource type
	 *
	 * @param resourceType Resource type
	 */
	public getResourceView(resourceType: RESOURCE_TYPES): string {
		return this.resourceTypeView[resourceType as string] || this.fallbackResourceTypeView;
	}

	/**
	 * Registers command ID to create resource for a given type
	 *
	 * @param resourceType Resource type
	 * @param commandId Command ID
	 */
	public registerResourceCreateCommand(resourceType: RESOURCE_TYPES, commandId: string): void {
		if (this.resourceTypeCreateCommand[resourceType as string]) {
			throw new Error(`Command for a resource type '${resourceType}' is already registered.`);
		}

		this.resourceTypeCreateCommand[resourceType as string] = commandId;
	}

	/**
	 * Returns command ID registered for a given resource type to create it
	 *
	 * @param resourceType Resource type
	 */
	public getResourceCreateCommandId(resourceType: RESOURCE_TYPES): string {
		return this.resourceTypeCreateCommand[resourceType as string] || null;
	}

	/**
	 * Creates resource from descriptor
	 *
	 * @param descriptor
	 */
	private upsertResource(
		descriptor: IResourceDescription,
		updateIndexes: boolean
	): {
		resource: TEditorResource;
		changes: IResourceChanges;
	} {
		let resource: TEditorResource;

		const changeResult: IResourceChanges = {
			descriptorChanged: false,
			resourceChanged: false,
			typesChanged: new Set<RESOURCE_TYPES>()
		};

		// Update
		if (this.resourceIndexById[descriptor.id]) {
			resource = this.resourceIndexById[descriptor.id];

			// Check if resource type has changed
			if (resource.resourceType !== descriptor.resourceType) {
				if (updateIndexes) {
					// Remove from type index
					const i = this.resourceIndexByType[resource.resourceType].indexOf(resource);

					if (i >= 0) {
						this.resourceIndexByType[resource.resourceType].splice(i, 1);
					}

					// Add to a new index
					if (!this.resourceIndexByType[descriptor.resourceType]) {
						this.resourceIndexByType[descriptor.resourceType] = [];
					}

					this.resourceIndexByType[descriptor.resourceType].push(resource);
				}

				// Update type
				resource.resourceType = descriptor.resourceType as TBlueprintResourceTypes;

				// Mark changes
				changeResult.descriptorChanged = true;
				changeResult.typesChanged.add(resource.resourceType);
				changeResult.typesChanged.add(descriptor.resourceType);
			}

			// Check last modified
			if (resource.descriptor.lastModified !== descriptor.lastModified) {
				changeResult.descriptorChanged = true;
			}

			// Check if path or name has changed
			if (
				resource.descriptor.name !== descriptor.name ||
				resource.descriptor.basePath !== descriptor.basePath
			) {
				// Update name and path
				resource.descriptor.name = descriptor.name;
				resource.descriptor.basePath = descriptor.basePath;

				if (updateIndexes) {
					const oldPath = getResourcePath(resource.descriptor);
					const newPath = getResourcePath(descriptor);

					delete this.resourceIndexByPath[oldPath];
					this.resourceIndexByPath[newPath] = resource;
				}

				changeResult.descriptorChanged = true;
			}

			// Check if parse data has changed
			if (!dataEqual(resource.descriptor.parseData, descriptor.parseData)) {
				this.processParseData(resource, descriptor);
				changeResult.descriptorChanged = true;
				changeResult.typesChanged.add(resource.resourceType);
			}

			// Create
		} else {
			resource = {
				resourceType: descriptor.resourceType,
				id: descriptor.id,
				descriptor: null,
				metaData: {
					modifiedAt: null,
					modifiedBy: null,
					lockedAt: null,
					lockedBy: null,
					revision: null
				},
				onChange: createEventEmitter<void>(),
				onDelete: createEventEmitter<IEditorResourceDeleteEvent>()
			} as TEditorResource;

			const path = getResourcePath(descriptor, { sanitize: true });

			this.processParseData(resource, descriptor);

			if (updateIndexes) {
				this.resourceIndexById[resource.id] = resource;
				this.resourceIndexByPath[path] = resource;

				if (!this.resourceIndexByType[descriptor.resourceType]) {
					this.resourceIndexByType[descriptor.resourceType] = [];
				}

				this.resourceIndexByType[descriptor.resourceType].push(resource);
			}

			changeResult.typesChanged.add(resource.resourceType);
			changeResult.descriptorChanged = true;
		}

		// Set descriptor
		resource.descriptor = {
			id: descriptor.id,
			resourceType: descriptor.resourceType,
			name: descriptor.name,
			basePath: descriptor.basePath,
			lastModified: descriptor.lastModified,
			// @TODO: Consult if blueprint content shouldn't have attributes and remove this hotfix
			attributes: descriptor.attributes ?? resource.descriptor?.attributes,
			parseData: descriptor.parseData,
			dependencies: descriptor.dependencies
		};

		return {
			resource: resource,
			changes: changeResult
		};
	}

	/**
	 * Updates resource entry and returns if was changed
	 * Does not emit change event on resource object!
	 * Emits only global type and list change events.
	 *
	 * @param resourceId Resource ID
	 * @param metaData Descriptor with meta data
	 */
	private updateResourceWithMetaData(metaData: IBlueprintMetadata): IResourceChanges {
		const { resource, changes } = this.upsertResource(metaData, true);

		const _resourceLockedAt = resource.metaData.lockedAt
			? resource.metaData.lockedAt.toISOString()
			: null;
		const _resourceModifiedAt = resource.metaData.modifiedAt
			? resource.metaData.modifiedAt.toISOString()
			: null;

		let assetResourceChanged = false;

		if (resource.resourceType === RESOURCE_TYPES.ASSET) {
			assetResourceChanged =
				resource.metaData.assetFilename !== metaData.metadata.assetFilename ||
				resource.metaData.assetMimetype !== metaData.metadata.assetMimetype ||
				resource.metaData.assetUrl !== metaData.metadata.assetUrl ||
				resource.metaData.assetSize !== metaData.metadata.assetSize;
		}

		if (
			assetResourceChanged ||
			_resourceLockedAt !== metaData.lockedAt ||
			resource.metaData.lockedBy !== metaData.lockedBy ||
			_resourceModifiedAt !== metaData.modifiedAt ||
			resource.metaData.modifiedBy !== metaData.modifiedBy ||
			resource.metaData.revision !== metaData.revision
		) {
			resource.metaData.lockedAt = metaData.lockedAt ? new Date(metaData.lockedAt) : null;
			resource.metaData.lockedBy = metaData.lockedBy;
			resource.metaData.modifiedAt = metaData.modifiedAt ? new Date(metaData.modifiedAt) : null;
			resource.metaData.modifiedBy = metaData.modifiedBy;
			resource.metaData.revision = metaData.revision;

			if (resource.resourceType === RESOURCE_TYPES.ASSET) {
				resource.metaData.assetFilename = metaData.metadata.assetFilename;
				resource.metaData.assetMimetype = metaData.metadata.assetMimetype;
				resource.metaData.assetUrl = metaData.metadata.assetUrl;
				resource.metaData.assetSize = metaData.metadata.assetSize;
			}

			changes.resourceChanged = true;
		}

		return changes;
	}

	/**
	 * Processes parse data based on resource type
	 *
	 * @param resource Resource
	 * @param descriptor Descriptor
	 */
	private processParseData(resource: TEditorResource, descriptor: IResourceDescription): void {
		switch (resource.resourceType) {
			case RESOURCE_TYPES.ACTION:
			case RESOURCE_TYPES.VIEW: {
				try {
					if (!descriptor.parseData.paramsSchema) {
						resource.paramsSchema = null;
					} else {
						const paramsSchema = importSchema<
							ISchemaValue<ISchemaConstObject<TSchemaConstObjectProps>>
						>(descriptor.parseData.paramsSchema);

						resource.paramsSchema = paramsSchema.constSchema.opts.props;
					}
				} catch (err) {
					this.logger.error(`Failed to import params schema for resource:`, descriptor);
					this.logger.debug("Params schema import error:", err);
					resource.paramsSchema = null;
				}
				break;
			}
			case RESOURCE_TYPES.INTEGRATION: {
				resource.integrationType = descriptor.parseData.integrationType;
				break;
			}
		}
	}

	/**
	 * Emits events based on resource type changes
	 *
	 * @param resource Resource
	 * @param changes Changes
	 */
	private handleResourceChange(resource: TEditorResource, changes: IResourceChanges): void {
		if (changes.descriptorChanged) {
			emitEvent(this.onResourceListChange);
		}

		for (const resourceType of changes.typesChanged) {
			emitEvent(this.onResourceTypeChange, resourceType);
		}

		if (changes.descriptorChanged || changes.resourceChanged) {
			emitEvent(resource.onChange);
		}

		if (changes.resourceChanged) {
			emitEvent(this.onResourceContentChange, resource);
		}
	}

	/**
	 * Removes resource from local cache
	 * If resource does not exists nothing will happen.
	 *
	 * @param resourceId Resource ID
	 * @param deletedByLocalAction If resource was deleted by local action
	 */
	private removeResourceFromCache(resourceId: string, deletedByLocalAction: boolean) {
		const resource = this.resourceIndexById[resourceId];

		if (!resource) {
			return;
		}

		// Remove from resource list
		const i = this.resourceList.indexOf(resource);

		if (i >= 0) {
			this.resourceList.splice(i, 1);
		}

		// Remove from type index
		const j = this.resourceIndexByType[resource.resourceType].indexOf(resource);

		if (j >= 0) {
			this.resourceIndexByType[resource.resourceType].splice(j, 1);
		}

		// Remove from path index
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		delete this.resourceIndexByPath[path];

		// Remove from ID index
		delete this.resourceIndexById[resourceId];

		// Emit events
		emitEvent(this.onResourceListChange);
		emitEvent(this.onResourceTypeChange, resource.resourceType);
		emitEvent(resource.onDelete, {
			deletedByLocalAction: deletedByLocalAction
		});
	}

	/**
	 * Handles reload timer
	 */
	private handleReload(loadList = true) {
		if (loadList) {
			this.loadResourceList();
			this.loadRouteList();
		}

		this.reloadTimer = setTimeout(() => this.handleReload(), RELOAD_INTERVAL);
	}

	/**
	 * Loads resource list
	 */
	public async loadResourceList() {
		try {
			const apiRes = await this.apiService.getResourceList();

			let listChanged = apiRes.length !== this.resourceList.length;
			const typesChanged = new Set<RESOURCE_TYPES>();

			// Update resource list
			this.resourceList = apiRes.map((descriptor) => {
				const { resource, changes } = this.upsertResource(descriptor, false);

				if (changes.descriptorChanged) {
					listChanged = true;
				}

				for (const resourceType of changes.typesChanged) {
					typesChanged.add(resourceType);
				}

				return resource;
			});

			// Rebuild indexes
			const oldIndexById = this.resourceIndexById;
			this.rebuildIndexesFromResourceList();

			// Emit delete events on removed items
			for (const k in oldIndexById) {
				if (!this.resourceIndexById[k]) {
					emitEvent(oldIndexById[k].onDelete, {
						deletedByLocalAction: false
					});
				}
			}

			// Emit changes
			if (listChanged) {
				this.logger.debug("[ Editor/ResourceService] Resource list updated:", this.resourceList);
				emitEvent(this.onResourceListChange);
			}

			for (const resourceType of typesChanged) {
				emitEvent(this.onResourceTypeChange, resourceType);
			}
		} catch (err) {
			// @todo Indicate error somehow visually
			this.logger.error("Failed to load resource list:", err);
		}
	}

	/**
	 * Rebuilds indexes from the current resource list
	 */
	private rebuildIndexesFromResourceList() {
		this.resourceIndexById = {};
		this.resourceIndexByType = {};
		this.resourceIndexByPath = {};
		this.referencedByIndex = new Map();

		for (let i = 0; i < this.resourceList.length; i++) {
			const item = this.resourceList[i];
			const path = getResourcePath(item.descriptor, { sanitize: true });

			// Add type index
			if (!this.resourceIndexByType[item.resourceType]) {
				this.resourceIndexByType[item.resourceType] = [];
			}

			this.resourceIndexByType[item.resourceType].push(item);
			this.resourceIndexByPath[path] = item;
			this.resourceIndexById[item.id] = item;

			// Add referenced by index
			for (let j = 0; j < item.descriptor.dependencies.length; j++) {
				const dep = item.descriptor.dependencies[j];

				if (!this.referencedByIndex.has(dep)) {
					this.referencedByIndex.set(dep, new Set([ item.id ]));
				} else {
					this.referencedByIndex.get(dep).add(item.id);
				}
			}
		}
	}

	/**
	 * Loads a route list
	 */
	private async loadRouteList() {
		try {
			const apiRes = await this.apiService.getRouteList();

			const listChanged = !dataEqual(this.routeList, apiRes);

			this.routeList = apiRes;
			this.routeIndex = {};

			for (let i = 0; i < this.routeList.length; i++) {
				const route = this.routeList[i];
				this.routeIndex[route.key] = route;
			}

			// Emit changes
			if (listChanged) {
				this.logger.debug("[ Editor/ResourceService] Route list updated:", this.routeList);
				emitEvent(this.onRouteListChange);
			}
		} catch (err) {
			// @todo Indicate error somehow visually
			this.logger.error("Failed to load route list:", err);
		}
	}

	/**
	 * Loads a route list
	 */
	private async loadIconPacks() {
		try {
			const apiRes = await this.apiService.getIconPacks();

			const hasChanged = !dataEqual(this.iconPacks, apiRes);

			this.iconPacks = apiRes.iconPacks;
			this.iconPackUrlMapping = apiRes.urlMapping;

			// Build index
			this.iconPackIndexByName = {};

			for (let i = 0; i < this.iconPacks.length; i++) {
				const iconPack = this.iconPacks[i];
				this.iconPackIndexByName[iconPack.name] = iconPack;
			}

			// Emit changes
			if (hasChanged) {
				this.logger.debug(
					"[ Editor/ResourceService] Icon packs loaded:",
					this.iconPacks,
					this.iconPackUrlMapping
				);
				emitEvent(this.onIconPacksChanged);
			}
		} catch (err) {
			// @todo Indicate error somehow visually
			this.logger.error("Failed to load icon packs:", err);
		}
	}

	/**
	 * Returns resource list
	 */
	public getResourceList(): TEditorResource[] {
		return this.resourceList;
	}

	/**
	 * Returns list of resources of a given type
	 *
	 * @param resourceType Resource type
	 */
	public getResourceListByType<TResourceType extends RESOURCE_TYPES>(
		resourceType: TResourceType
	): TEditorResourceByType<TResourceType>[] {
		return (this.resourceIndexByType[resourceType] || []) as TEditorResourceByType<TResourceType>[];
	}

	/**
	 * Returns resource by ID
	 *
	 * @param resourceId Resource ID
	 */
	public getResourceById(resourceId: string): TEditorResource {
		return this.resourceIndexById[resourceId] || null;
	}

	/**
	 * Returns resource descriptor by ID
	 *
	 * @param resourceId Resource ID
	 */
	public getResourceDescriptorById(resourceId: string): IResourceDescription {
		return this.resourceIndexById[resourceId] ? this.resourceIndexById[resourceId].descriptor : null;
	}

	/**
	 * Returns resource by path
	 *
	 * @param path Resource path
	 */
	public getResourceByPath(path: string): TEditorResource {
		return this.resourceIndexByPath[path] || null;
	}

	/**
	 * Returns resource descriptor by path
	 *
	 * @param path Resource path
	 */
	public getResourceDescriptorByPath(path: string): IResourceDescription {
		return this.resourceIndexByPath[path] ? this.resourceIndexByPath[path].descriptor : null;
	}

	/**
	 * Returns resource descriptors for all parent resources
	 *
	 * @param resourceId Resource ID
	 */
	public getResourceParentList(resourceId: string): IResourceDescription[] {
		const resource = this.resourceIndexById[resourceId];

		if (!resource) {
			return null;
		}

		const path = parseBasePath(resource.descriptor.basePath);
		const tmpPath = [];
		const res: IResourceDescription[] = [];

		for (let i = 0; i < path.length; i++) {
			tmpPath.push(path[i]);
			res.push(this.getResourceDescriptorByPath(tmpPath.join("/")));
		}

		return res;
	}

	/**
	 * Returns list of resources at a specific base path (direct children only)
	 *
	 * @param basePath Base path to search for resources
	 */
	public getResourceListByBasePath(basePath: string): TEditorResource[] {
		// Normalize the basePath for comparison (empty string for root, or path without trailing slash)
		const normalizedBasePath = basePath.replace(/\/$/, "");

		return this.resourceList.filter(
			(resource) => resource.descriptor.basePath === normalizedBasePath
		);
	}

	/**
	 * Returns resource contents with meta-data
	 *
	 * @param resourceId Resource ID
	 */
	public async getBlueprintById(
		resourceId: string,
		{ allowUnknownType }: { allowUnknownType?: boolean } = {}
	): Promise<TEditorBlueprintResource> {
		const resource = this.resourceIndexById[resourceId] as TEditorBlueprintResource;

		if (!resource) {
			return null;
		}

		if (!allowUnknownType && !BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
			throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
		}

		const path = getResourcePath(resource.descriptor, { sanitize: true });
		// @ts-ignore
		const blueprint = await (resource.resourceType === RESOURCE_TYPES.MANIFEST
			? this.apiService.getManifest()
			: this.apiService.getBlueprintContent(path));

		if (!blueprint) {
			return null;
		}

		// Update resource
		const changes = this.updateResourceWithMetaData(blueprint);

		if (!allowUnknownType && !BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a blueprint type anymore.`
			);
		}

		// Update content
		resource.content = blueprint.content;
		resource.isContentUpToDate = true;

		this.handleResourceChange(resource, changes);

		return resource;
	}

	/**
	 * Check if resource is blueprint type
	 *
	 * @param resource
	 */
	public isBlueprintResource(resource: TEditorResource): resource is TEditorBlueprintResource {
		return BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType);
	}

	/**
	 * Check if resource is directory type
	 *
	 * @param resource
	 */
	public isDirectoryResource(resource: TEditorResource): resource is IEditorResource_Directory {
		return resource.resourceType === RESOURCE_TYPES.DIRECTORY;
	}

	/**
	 * Creates a new blueprint resource
	 *
	 * @param basePath Base path
	 * @param name Name
	 * @param content Blueprint content
	 */
	public async createBlueprint(
		basePath: string,
		name: string,
		content: string,
		resourceId: string
	): Promise<TEditorBlueprintResource> {
		// Create blueprint
		const res = await this.apiService.createBlueprint(basePath, name, content);

		// Update resource
		const changes = this.updateResourceWithMetaData(res);

		const resource = this.resourceIndexById[resourceId] as TEditorBlueprintResource;

		if (resource === null) {
			throw new Error("Unexpected error - newly created blueprint is not in resource index.");
		}

		// Update content
		resource.content = content;
		resource.isContentUpToDate = true;

		this.handleResourceChange(resource, changes);

		return resource;
	}

	/**
	 * Updates blueprint resource contents
	 *
	 * @param resourceId Resource ID to update
	 * @param content New content
	 * @param sourceRevision Source revision
	 * @param commitMessage Commit message
	 */
	public async saveBlueprint(
		resourceId: string,
		content: string,
		sourceRevision: string,
		commitMessage: string
	): Promise<TEditorBlueprintResource> {
		const resource = this.resourceIndexById[resourceId] as TEditorBlueprintResource;

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
			throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
		}

		// Write blueprint
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		const res = await this.apiService.updateBlueprintContent(
			path,
			content,
			sourceRevision,
			commitMessage
		);

		// Update resource
		const changes = this.updateResourceWithMetaData(res);

		if (!BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a blueprint type anymore.`
			);
		}

		// Update content
		resource.content = content;
		resource.isContentUpToDate = true;

		this.handleResourceChange(resource, changes);

		return resource;
	}

	/**
	 * Deletes blueprint resource
	 *
	 * @param resourceId Resource ID to delete
	 */
	public async deleteBlueprint(resourceId: string): Promise<void> {
		const resource = this.resourceIndexById[resourceId] as TEditorBlueprintResource;

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
			throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
		}

		// Delete blueprint
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		await this.apiService.deleteBlueprint(path);

		this.removeResourceFromCache(resourceId, true);
	}

	///**
	// * Deletes asset resource
	// *
	// * @param resourceId Resource ID to delete
	// */
	//public async deleteAsset(resourceId: string): Promise<void> {
	//	const resource = this.resourceIndexById[resourceId] as TEditorBlueprintResource;

	//	if (!resource) {
	//		throw new Error(`Resource ID '${resourceId}' does not exists.`);
	//	}

	//	if (!BLUEPRINT_RESOURCE_TYPES.includes(resource.resourceType)) {
	//		throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
	//	}

	//	// Delete blueprint
	//	const path = getResourcePath(resource.descriptor, { sanitize: true });

	//	if (resource.resourceType === RESOURCE_TYPES.ASSET) {
	//		await this.apiService.deleteAsset(resourceId);
	//	} else {
	//		await this.apiService.deleteBlueprint(path);
	//	}

	//	this.removeResourceFromCache(resourceId, true);

	//}

	/**
	 * Renames blueprint resource
	 *
	 * @param resourceId Resource ID to update
	 * @param name New name
	 */
	public async renameBlueprint(resourceId: string, name: string): Promise<TEditorBlueprintResource> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!this.isBlueprintResource(resource)) {
			throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
		}

		// Write changes
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		const basePath = resource.descriptor.basePath;

		const res = await this.apiService.renameAndMoveBlueprint({
			path,
			basePath,
			name
		});

		// Update resource
		const changes = this.updateResourceWithMetaData(res);

		if (!this.isBlueprintResource(resource)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a blueprint type anymore.`
			);
		}

		await this.loadResourceList();

		const updatedResource = await this.getBlueprintById(resourceId);
		this.handleResourceChange(updatedResource, changes);

		return resource;
	}

	/**
	 * Moves blueprint resource
	 *
	 * @param resourceId Resource ID to update
	 * @param targetId New parent directory
	 */
	public async moveBlueprint(resourceId: string, targetId?: string): Promise<TEditorBlueprintResource> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!this.isBlueprintResource(resource)) {
			throw new Error(`Resource with ID '${resourceId}' is not a blueprint type.`);
		}

		const targetIsRoot = !targetId;
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		const name = resource.descriptor.name;

		let basePath = "";

		if (!targetIsRoot) {
			const targetResource = this.getResourceById(targetId);

			if (!targetResource) {
				throw new Error(`Resource ID '${targetId}' does not exists.`);
			}

			if (!this.isDirectoryResource(targetResource)) {
				throw new Error(`Resource with ID '${targetId}' is not a directory type.`);
			}

			basePath = getResourcePath(targetResource.descriptor);
		}

		const res = await this.apiService.renameAndMoveBlueprint({
			path,
			basePath,
			name
		});

		// Update directory
		const changes = this.updateResourceWithMetaData(res);

		if (!this.isBlueprintResource(resource)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a blueprint type anymore.`
			);
		}

		await this.loadResourceList();

		const updatedResource = await this.getBlueprintById(resourceId);
		this.handleResourceChange(updatedResource, changes);

		return resource;
	}

	/**
	 * Creates a new blueprint resource
	 *
	 * @param fromResourceId Base path
	 * @param toResourceId Blueprint content
	 * @param name Name
	 */
	public async duplicateBlueprint(
		fromResourceId: string,
		name: string,
		toResourceId: string
	): Promise<TEditorBlueprintResource> {
		const resource = this.getResourceById(fromResourceId);

		if (!resource) {
			throw new Error(`Resource ID '${fromResourceId}' does not exists.`);
		}

		if (!this.isBlueprintResource(resource)) {
			throw new Error(`Resource with ID '${fromResourceId}' is not a blueprint type.`);
		}

		const freshResource = await this.getBlueprintById(fromResourceId);
		const content = freshResource.content;
		const basePath = freshResource.descriptor.basePath;

		const dCtx = new DesignContext({
			resolvers: {},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: false
		});

		const blueprintModel = dCtx.loadYAMLDocument(BlueprintBase, content, "local://blueprint");

		blueprintModel.props.id.schema.setValue(blueprintModel.props.id, toResourceId);
		blueprintModel.props.attributes.props.allowRemove.schema.setValue(
			blueprintModel.props.attributes.props.allowRemove,
			true
		);
		blueprintModel.props.attributes.props.allowRename.schema.setValue(
			blueprintModel.props.attributes.props.allowRename,
			true
		);
		blueprintModel.props.attributes.props.allowWrite.schema.setValue(
			blueprintModel.props.attributes.props.allowWrite,
			true
		);
		blueprintModel.props.attributes.props.hidden.schema.setValue(
			blueprintModel.props.attributes.props.hidden,
			false
		);

		const newContent = serializeIDTToYAML(blueprintModel.schema.serialize(blueprintModel, [ "$" ]));

		const newResource = await this.createBlueprint(basePath, name, newContent, toResourceId);

		if (!newResource) {
			throw new Error(`Newly created resource with ID '${toResourceId}' does not exists.`);
		}

		await this.loadResourceList();

		return newResource;
	}

	/**
	 * Import a new blueprint resource
	 *
	 * @param content Content blueprint
	 * @param basePath Base path
	 * @param name Name
	 * @param newResourceId Generated new blueprint ID
	 */
	public async importBlueprint(
		content: string,
		name: string,
		basePath: string,
		newResourceId: string
	): Promise<TEditorBlueprintResource> {
		const dCtx = new DesignContext({
			resolvers: {},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: false
		});

		const blueprintModel = dCtx.loadYAMLDocument(BlueprintBase, content, "local://blueprint");

		blueprintModel.props.id.schema.setValue(blueprintModel.props.id, newResourceId);
		blueprintModel.props.attributes.props.allowRemove.schema.setValue(
			blueprintModel.props.attributes.props.allowRemove,
			true
		);
		blueprintModel.props.attributes.props.allowRename.schema.setValue(
			blueprintModel.props.attributes.props.allowRename,
			true
		);
		blueprintModel.props.attributes.props.allowWrite.schema.setValue(
			blueprintModel.props.attributes.props.allowWrite,
			true
		);
		blueprintModel.props.attributes.props.hidden.schema.setValue(
			blueprintModel.props.attributes.props.hidden,
			false
		);

		const newContent = serializeIDTToYAML(blueprintModel.schema.serialize(blueprintModel, [ "$" ]));

		const newResource = await this.createBlueprint(basePath, name, newContent, newResourceId);

		if (!newResource) {
			throw new Error(`Newly created resource with ID '${newResourceId}' does not exists.`);
		}

		await this.loadResourceList();

		return newResource;
	}

	public async createAsset(basePath: string, files: FileList): Promise<void> {
		await this.apiService.addAssets(basePath, files);
		await this.loadResourceList();
	}

	public async updateAsset(assetId: string, files: FileList): Promise<void> {
		const resource = this.getResourceById(assetId);

		if (resource?.resourceType !== RESOURCE_TYPES.ASSET) {
			throw Error(`Resource with id ${assetId} is not asset.`);
		}

		const res = await this.apiService.updateAsset(assetId, files, resource.metaData?.revision);
		const changes = this.updateResourceWithMetaData(res);
		await this.loadResourceList();

		this.handleResourceChange(resource, changes);
	}

	/**
	 * Creates a new directory
	 *
	 * @param basePath Full path of directory
	 * @param name Full path of directory
	 */
	public async createDirectory(name: string, basePath = ""): Promise<void> {
		const fullPath = normalizeResourcePath(basePath, name);
		await this.apiService.createDirectory(fullPath);
		await this.loadResourceList();
	}

	public getResourceNormalizedPathById(resourceId: string): string | null {
		const resource = this.getResourceDescriptorById(resourceId);
		return normalizeResourcePath(resource?.basePath || "", resource?.name || "");
	}

	/**
	 * Deletes directory
	 *
	 * @param resourceId Resource ID to delete
	 */
	public async deleteDirectory(resourceId: string): Promise<void> {
		const resource = this.resourceIndexById[resourceId] as IEditorResource_Directory;

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!this.isDirectoryResource(resource)) {
			throw new Error(`Resource with ID '${resourceId}' is not a directory type.`);
		}

		// Delete directory
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		await this.apiService.deleteDirectory(path);

		this.removeResourceFromCache(resourceId, true);
		await this.loadResourceList();
	}

	/**
	 * Renames directory resource
	 *
	 * @param resourceId Resource ID to update
	 * @param name New name
	 */
	public async renameDirectory(resourceId: string, name: string): Promise<IEditorResource_Directory> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!this.isDirectoryResource(resource)) {
			throw new Error(`Resource with ID '${resourceId}' is not a directory type.`);
		}

		// Write changes
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		const basePath = resource.descriptor.basePath;

		await this.apiService.renameAndMoveDirectory({
			path,
			basePath,
			name
		});

		const update: IBlueprintMetadata = {
			...resource.descriptor,
			name
		};

		// Update directory
		const changes = this.updateResourceWithMetaData(update);

		if (!this.isDirectoryResource(resource)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a directory type anymore.`
			);
		}

		await this.loadResourceList();

		this.handleResourceChange(resource, changes);

		return resource;
	}

	/**
	 * Moves directory resource
	 *
	 * @param resourceId Resource ID to update
	 * @param targetId New parent directory
	 */
	public async moveDirectory(resourceId: string, targetId?: string): Promise<IEditorResource_Directory> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (!this.isDirectoryResource(resource)) {
			throw new Error(`Resource with ID '${resourceId}' is not a directory type.`);
		}

		const targetIsRoot = !targetId;
		const path = getResourcePath(resource.descriptor, { sanitize: true });
		const name = resource.descriptor.name;
		let basePath = "";

		if (!targetIsRoot) {
			const targetResource = this.getResourceById(targetId);

			if (!targetResource) {
				throw new Error(`Resource ID '${targetId}' does not exists.`);
			}

			if (!this.isDirectoryResource(targetResource)) {
				throw new Error(`Resource with ID '${targetId}' is not a directory type.`);
			}

			basePath = getResourcePath(targetResource.descriptor);
		}

		// Write changes
		await this.apiService.renameAndMoveDirectory({
			path,
			basePath,
			name
		});

		const update: IBlueprintMetadata = {
			...resource.descriptor,
			basePath,
			name
		};

		// Update directory
		const changes = this.updateResourceWithMetaData(update);

		if (!this.isDirectoryResource(resource)) {
			throw new Error(
				`Resource ID '${resourceId}' type has changed during update and is not a directory type anymore.`
			);
		}

		this.handleResourceChange(resource, changes);
		await this.loadResourceList();

		return resource;
	}

	/**
	 * Renames resource
	 *
	 * @param resourceId Resource ID to update
	 * @param name New name
	 */
	public async renameResource(resourceId: string, name: string): Promise<TEditorResource> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (this.isBlueprintResource(resource)) {
			return this.renameBlueprint(resourceId, name);
		}

		if (this.isDirectoryResource(resource)) {
			return this.renameDirectory(resourceId, name);
		}

		throw new Error(`Resource with ID '${resourceId}' is not a directory nor blueprint type.`);
	}

	/**
	 * Moves resource
	 *
	 * @param resourceId Resource ID to update
	 * @param targetId New parent directory
	 */
	public async moveResource(resourceId: string, targetId?: string): Promise<TEditorResource> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		if (targetId) {
			const targetResource = this.getResourceById(targetId);

			if (!targetResource) {
				throw new Error(`Resource ID '${resourceId}' does not exists.`);
			}

			if (!this.isDirectoryResource(targetResource)) {
				throw new Error(`Resource with ID '${targetId}' is not a directory type.`);
			}
		}

		if (this.isBlueprintResource(resource)) {
			return this.moveBlueprint(resourceId, targetId);
		}

		if (this.isDirectoryResource(resource)) {
			return this.moveDirectory(resourceId, targetId);
		}

		throw new Error(`Resource with ID '${resourceId}' is not a directory nor blueprint type.`);
	}

	/**
	 * Duplicates resource
	 *
	 * @param fromResourceId Resource ID to update
	 * @param name New name
	 * @param toResourceId Resource ID to update
	 */
	public async duplicateResource(
		fromResourceId: string,
		name: string,
		toResourceId: string
	): Promise<TEditorResource> {
		const resource = this.getResourceById(fromResourceId);

		if (!resource) {
			throw new Error(`Resource ID '${fromResourceId}' does not exists.`);
		}

		if (this.isBlueprintResource(resource)) {
			return this.duplicateBlueprint(fromResourceId, name, toResourceId);
		}

		if (this.isDirectoryResource(resource)) {
			throw new Error(`We can't duplicate directories yet.`);
		}

		throw new Error(`Resource with ID '${fromResourceId}' is not a directory nor blueprint type.`);
	}

	/**
	 * Deletes resource by ID
	 *
	 * @param resourceId Resource ID
	 */
	public async deleteResource(resourceId: string): Promise<void> {
		const resource = this.getResourceById(resourceId);

		if (!resource) {
			throw new Error(`Resource ID '${resourceId}' does not exists.`);
		}

		switch (resource.resourceType) {
			case RESOURCE_TYPES.DIRECTORY: {
				return this.deleteDirectory(resourceId);
			}
			case RESOURCE_TYPES.ACTION:
			case RESOURCE_TYPES.ASSET:
			case RESOURCE_TYPES.INTEGRATION:
			case RESOURCE_TYPES.README:
			case RESOURCE_TYPES.ROUTES:
			case RESOURCE_TYPES.THEME:
			case RESOURCE_TYPES.TRANSLATIONS:
			case RESOURCE_TYPES.ENDPOINT:
			case RESOURCE_TYPES.VIEW: {
				return this.deleteBlueprint(resourceId);
			}
			default: {
				throw new Error("Cannot delete unknown resource type.");
			}
		}
	}

	/**
	 * Returns a list of routes
	 */
	public getRouteList(): IRouteListEntry[] {
		return this.routeList;
	}

	/**
	 * Returns a route entry by name
	 *
	 * @param routeName Route name
	 */
	public getRouteByName(routeName: string): IRouteListEntry {
		return this.routeIndex[routeName] || null;
	}

	/**
	 * Returns icon packs
	 */
	public getIconPacks(): IIconPack[] {
		return this.iconPacks;
	}

	/**
	 * Returns icon pack by name
	 *
	 * @param iconPackName Icon pack name
	 */
	public getIconPackByName(iconPackName: string): IIconPack {
		return this.iconPackIndexByName[iconPackName] || null;
	}

	/**
	 * Returns mapping or icon pack name to URL prefix
	 */
	public getIconPackUrlMapping(): IIconPackageURLMapping {
		return this.iconPackUrlMapping;
	}

	/**
	 * Returns a list of referenced resources by resource ID
	 *
	 * @param resourceId Resource ID
	 * @returns 
	 */
	public getReferencedByResourceList(resourceId: string): TEditorResource[] {
		const refs = this.referencedByIndex.get(resourceId);

		if (refs) {
			return Array.from(refs).map((id) => this.resourceIndexById[id]);
		} else {
			return [];
		}
	}
}
